import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { adminApi, type PortalPermissions } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PORTAL_FEATURES } from './portalFeatures'

type Editable = Omit<PortalPermissions, 'feedback'>

const withoutFeedback = (p: PortalPermissions): Editable => ({
  staffManagement: p.staffManagement,
  bookingRequests: p.bookingRequests,
  invoices: p.invoices,
  editDetails: p.editDetails,
  assignAttendees: p.assignAttendees,
  bookSessions: p.bookSessions,
})

/** Settings -> Client portal: what clients can do by default. Each client can override this on their page. */
export default function PortalDefaultsCard({ onOpenFeedback }: { onOpenFeedback: () => void }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Editable | null>(null)
  const [saved, setSaved] = useState(false)

  const { data } = useQuery({
    queryKey: ['portal-defaults'],
    queryFn: () => adminApi.getPortalDefaults().then(r => r.data),
  })

  if (data && !draft) setDraft(withoutFeedback(data))

  const mutation = useMutation({
    mutationFn: () => adminApi.updatePortalDefaults(draft!),
    onSuccess: () => {
      // Show what was just saved straight away. (Clearing the draft here would re-seed it from the still-old
      // cached data, so the change would appear to revert until the page was refreshed.)
      queryClient.setQueryData<PortalPermissions>(['portal-defaults'], old => old && { ...old, ...draft! })
      queryClient.invalidateQueries({ queryKey: ['portal-defaults'] })
      queryClient.invalidateQueries({ queryKey: ['client-portal-settings'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  if (!data || !draft) return <p className="text-sm text-gray-400">Loading…</p>

  const current = withoutFeedback(data)
  const dirty = (Object.keys(draft) as (keyof Editable)[]).some(k => draft[k] !== current[k])

  return (
    <form onSubmit={e => { e.preventDefault(); mutation.mutate() }} className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader><CardTitle className="text-base">Client portal</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-gray-500">
            Choose what your clients can do in their portal. These are the defaults for every client;
            you can change them for an individual client from their page (Portal tab).
            Viewing their team, compliance, certificates and bookings is always on.
          </p>

          <div className="space-y-4">
            {PORTAL_FEATURES.map(f => f.key === 'feedback' ? (
              <div key={f.key} className="flex items-start gap-3">
                <input type="checkbox" checked={data.feedback} disabled readOnly className="mt-0.5 w-4 h-4 rounded border-gray-300" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{f.label}</p>
                  <p className="text-xs text-gray-500">
                    {f.description} This follows the "Enable feedback" switch.{' '}
                    <button type="button" onClick={onOpenFeedback} className="text-indigo-600 hover:text-indigo-800 cursor-pointer">
                      Change in Feedback settings
                    </button>
                  </p>
                </div>
              </div>
            ) : (
              <label key={f.key} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft[f.key as keyof Editable]}
                  onChange={e => setDraft({ ...draft, [f.key]: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{f.label}</p>
                  <p className="text-xs text-gray-500">{f.description}</p>
                </div>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" disabled={!dirty || mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
            {saved && <span className="flex items-center gap-1 text-sm text-green-600"><Check size={14} /> Saved</span>}
            {mutation.isError && <span className="text-sm text-red-600">Could not save.</span>}
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
